import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import api from '../../services/api';
import { 
  ArrowLeft, Check, X, Zap, TrendingUp, Star, Crown, Shield,
  Users, FileText, MapPin, HardDrive, Building, Sparkles,
  ChevronRight, Loader2
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const PlansPage = () => {
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [subscribing, setSubscribing] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');

  const bid = activeBusiness?.id;

  useEffect(() => {
    if (bid) {
      fetchData();
    }
  }, [bid]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [plansRes, subRes] = await Promise.all([
        api.get('/billing/plans').catch(() => null),
        api.get('/business/subscription').catch(() => null),
      ]);

      if (plansRes?.data) {
        setPlans(Array.isArray(plansRes.data) ? plansRes.data : []);
      }

      if (subRes?.data?.subscription) {
        const s = subRes.data.subscription;
        setCurrentSubscription({
          plan_code: s.plan?.code?.toUpperCase() || 'BASIC',
          plan_name: s.plan?.name || 'Basic Plan',
          status: (s.status || 'active').toUpperCase(),
        });
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planCode) => {
    if (currentSubscription && currentSubscription.plan_code !== 'FREE') {
      toast.error('Please cancel your current subscription first to change plans');
      return;
    }

    try {
      setSubscribing(planCode);

      const response = await api.post('/billing/subscribe', {
        plan_code: planCode,
        billing_cycle: billingCycle,
      });

      toast.success(response.data?.message || 'Successfully subscribed!');
      navigate(`/business/${bid}/billing`);
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error(error.response?.data?.detail || 'Failed to subscribe to plan');
    } finally {
      setSubscribing(null);
    }
  };

  const getPlanConfig = (code) => {
    const configs = {
      FREE: {
        gradient: 'from-slate-500 to-slate-600',
        lightBg: 'bg-slate-50',
        border: 'border-slate-200',
        icon: Shield,
        popular: false
      },
      STARTER: {
        gradient: 'from-teal-500 to-emerald-600',
        lightBg: 'bg-teal-50',
        border: 'border-teal-200',
        icon: Zap,
        popular: false
      },
      GROWTH: {
        gradient: 'from-blue-500 to-indigo-600',
        lightBg: 'bg-blue-50',
        border: 'border-blue-300',
        icon: TrendingUp,
        popular: true
      },
      PRO: {
        gradient: 'from-purple-500 to-pink-600',
        lightBg: 'bg-purple-50',
        border: 'border-purple-200',
        icon: Star,
        popular: false
      },
      ENTERPRISE: {
        gradient: 'from-amber-500 to-orange-600',
        lightBg: 'bg-amber-50',
        border: 'border-amber-200',
        icon: Crown,
        popular: false
      }
    };
    return configs[code] || configs.FREE;
  };

  const getYearlySavings = (monthlyPrice) => {
    const yearlyPrice = monthlyPrice * 10; // 2 months free
    const savings = (monthlyPrice * 12) - yearlyPrice;
    return savings;
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <div className="w-12 h-12 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[14px] text-slate-500">Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-fadeIn">
      {/* ════════════════════════════════════════════════════════════════════════
          PREMIUM PAGE HEADER
          ════════════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/business/${bid}/billing`)}
                className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div>
                <h1 className="text-[22px] font-bold text-slate-800 tracking-tight">Choose Your Plan</h1>
                <p className="text-[13px] text-slate-500 mt-0.5">Select the perfect plan for your business needs</p>
              </div>
            </div>

            {/* Billing Cycle Toggle */}
            <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-xl">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 ${
                  billingCycle === 'monthly'
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-4 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 flex items-center gap-2 ${
                  billingCycle === 'yearly'
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                Yearly
                <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded">
                  Save 17%
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          MAIN CONTENT
          ════════════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 overflow-y-auto px-6 py-8">
        <div className="max-w-[1400px] mx-auto">
          
          {/* Current Plan Notice */}
          {currentSubscription && currentSubscription.plan_code !== 'FREE' && (
            <div className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-5 flex items-center justify-between animate-slideDown">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Star className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-[14px] font-semibold text-blue-800">
                    Currently on {currentSubscription.plan_name}
                  </p>
                  <p className="text-[13px] text-blue-600">
                    {currentSubscription.status === 'ACTIVE' ? 'Active subscription' : currentSubscription.status}
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/business/${bid}/billing`)}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl text-[13px] font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                Manage Subscription
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan, index) => {
              const config = getPlanConfig(plan.code);
              const Icon = config.icon;
              const isCurrentPlan = currentSubscription?.plan_code === plan.code;
              const price = billingCycle === 'yearly' 
                ? (plan.price_monthly * 10).toFixed(0) 
                : plan.price_monthly;
              const monthlyEquivalent = billingCycle === 'yearly'
                ? ((plan.price_monthly * 10) / 12).toFixed(0)
                : plan.price_monthly;

              return (
                <div
                  key={plan.id}
                  className={`relative bg-white rounded-2xl border-2 overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                    config.popular ? 'border-blue-400 shadow-lg shadow-blue-100' : config.border
                  } ${isCurrentPlan ? 'ring-2 ring-emerald-400 ring-offset-2' : ''}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Popular Badge */}
                  {config.popular && (
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-center py-2 text-[11px] font-bold uppercase tracking-wider">
                      <Sparkles className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                      Most Popular
                    </div>
                  )}

                  {/* Current Plan Badge */}
                  {isCurrentPlan && (
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-center py-2 text-[11px] font-bold uppercase tracking-wider">
                      <Check className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                      Current Plan
                    </div>
                  )}

                  <div className={`p-6 ${config.popular || isCurrentPlan ? 'pt-12' : ''}`}>
                    {/* Plan Header */}
                    <div className="text-center mb-6">
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-[20px] font-bold text-slate-800">{plan.name}</h3>
                      <p className="text-[13px] text-slate-500 mt-1">{plan.description || 'Perfect for getting started'}</p>
                    </div>

                    {/* Pricing */}
                    <div className="text-center mb-6">
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-[14px] text-slate-500">₹</span>
                        <span className="text-[42px] font-bold text-slate-800 leading-none">
                          {plan.price_monthly === 0 ? '0' : monthlyEquivalent}
                        </span>
                        <span className="text-[14px] text-slate-500">/mo</span>
                      </div>
                      {billingCycle === 'yearly' && plan.price_monthly > 0 && (
                        <p className="text-[12px] text-emerald-600 font-medium mt-1">
                          ₹{price} billed yearly (Save ₹{getYearlySavings(plan.price_monthly)})
                        </p>
                      )}
                    </div>

                    {/* Features */}
                    <div className="space-y-3 mb-6">
                      {plan.features?.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full ${config.lightBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                            <Check className={`w-3 h-3 ${config.gradient.includes('slate') ? 'text-slate-600' : 'text-emerald-600'}`} />
                          </div>
                          <span className="text-[13px] text-slate-600">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Limits Section */}
                    <div className={`${config.lightBg} rounded-xl p-4 mb-6`}>
                      <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold mb-3">Plan Limits</p>
                      <div className="space-y-2">
                        <LimitItem icon={Users} label="Staff Members" value={plan.limits?.staff_limit || '∞'} />
                        <LimitItem icon={Building} label="Retailers" value={plan.limits?.retailers_limit || '∞'} />
                        <LimitItem icon={FileText} label="Invoices/Month" value={plan.limits?.invoices_per_month || '∞'} />
                        <LimitItem icon={MapPin} label="GPS Points/Day" value={plan.limits?.gps_points_per_day || '∞'} />
                        <LimitItem icon={HardDrive} label="Storage" value={`${plan.limits?.storage_gb || 10} GB`} />
                      </div>
                    </div>

                    {/* CTA Button */}
                    {isCurrentPlan ? (
                      <button
                        disabled
                        className="w-full py-3.5 bg-slate-100 text-slate-500 rounded-xl font-semibold text-[14px] cursor-not-allowed"
                      >
                        Current Plan
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSubscribe(plan.code)}
                        disabled={subscribing === plan.code}
                        className={`w-full py-3.5 rounded-xl font-semibold text-[14px] transition-all duration-200 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                          config.popular
                            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/30'
                            : `bg-gradient-to-r ${config.gradient} text-white shadow-md hover:shadow-lg`
                        }`}
                      >
                        {subscribing === plan.code ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Processing...
                          </>
                        ) : plan.price_monthly === 0 ? (
                          'Get Started Free'
                        ) : (
                          <>
                            Subscribe Now
                            <ChevronRight className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Feature Comparison Table */}
          <div className="mt-12 bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-transparent">
              <h3 className="text-[16px] font-semibold text-slate-800">Feature Comparison</h3>
              <p className="text-[13px] text-slate-500 mt-0.5">Compare all features across plans</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-6 py-4 text-[12px] font-semibold text-slate-500 uppercase tracking-wider">Feature</th>
                    {plans.map(plan => (
                      <th key={plan.id} className="text-center px-4 py-4 text-[12px] font-semibold text-slate-700 uppercase tracking-wider">
                        {plan.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <ComparisonRow label="Staff Members" plans={plans} getValue={(p) => p.limits?.staff_limit || '∞'} />
                  <ComparisonRow label="Retailers" plans={plans} getValue={(p) => p.limits?.retailers_limit || '∞'} />
                  <ComparisonRow label="Invoices/Month" plans={plans} getValue={(p) => p.limits?.invoices_per_month || '∞'} />
                  <ComparisonRow label="GPS Tracking" plans={plans} getValue={(p) => p.limits?.gps_points_per_day ? `${p.limits.gps_points_per_day}/day` : '∞'} />
                  <ComparisonRow label="Storage" plans={plans} getValue={(p) => `${p.limits?.storage_gb || 10} GB`} />
                  <ComparisonRow label="Priority Support" plans={plans} getValue={(p) => p.code !== 'FREE'} isBoolean />
                  <ComparisonRow label="Advanced Reports" plans={plans} getValue={(p) => ['GROWTH', 'PRO', 'ENTERPRISE'].includes(p.code)} isBoolean />
                  <ComparisonRow label="API Access" plans={plans} getValue={(p) => ['PRO', 'ENTERPRISE'].includes(p.code)} isBoolean />
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="mt-8 text-center">
            <p className="text-[13px] text-slate-500 mb-2">
              Need a custom plan for your enterprise?
            </p>
            <button className="text-[14px] text-teal-600 hover:text-teal-700 font-semibold">
              Contact Sales →
            </button>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// LIMIT ITEM COMPONENT
// ════════════════════════════════════════════════════════════════════════════════
const LimitItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-slate-400" />
      <span className="text-[12px] text-slate-600">{label}</span>
    </div>
    <span className="text-[12px] font-semibold text-slate-700">{value}</span>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════════
// COMPARISON ROW COMPONENT
// ════════════════════════════════════════════════════════════════════════════════
const ComparisonRow = ({ label, plans, getValue, isBoolean = false }) => (
  <tr className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
    <td className="px-6 py-3.5 text-[13px] text-slate-700">{label}</td>
    {plans.map(plan => {
      const value = getValue(plan);
      return (
        <td key={plan.id} className="text-center px-4 py-3.5">
          {isBoolean ? (
            value ? (
              <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <Check className="w-3.5 h-3.5 text-emerald-600" />
              </div>
            ) : (
              <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
                <X className="w-3.5 h-3.5 text-slate-400" />
              </div>
            )
          ) : (
            <span className="text-[13px] font-medium text-slate-700">{value}</span>
          )}
        </td>
      );
    })}
  </tr>
);

export default PlansPage;
